import React, { useEffect, useState, useCallback } from 'react';
import { fetchMeals, setMeal, deleteMeal, fetchSavedMeals, fetchRoster, setMealCook } from '../api';

const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function getWeekDates() {
  const today = new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const str = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    return { str, day: DAYS_SHORT[d.getDay()], isToday: i === 0 };
  });
}

function InitialBadge({ name, color, size = 22 }) {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color + '33',
      border: `1.5px solid ${color}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <span style={{
        fontFamily: 'var(--font-mono)',
        fontSize: size * 0.38,
        color: color,
        fontWeight: 600,
        lineHeight: 1,
      }}>{initials}</span>
    </div>
  );
}

export default function MealPanel() {
  const [meals, setMeals]   = useState({});
  const [saved, setSaved]   = useState([]);
  const [roster, setRoster] = useState([]);
  const [editing, setEditing] = useState(null);
  const [inputVal, setInputVal] = useState('');
  const weekDates = getWeekDates();

  const load = useCallback(async () => {
    const start = weekDates[0].str;
    const end   = weekDates[6].str;
    const [data, savedData, rosterData] = await Promise.all([
      fetchMeals(start, end),
      fetchSavedMeals(),
      fetchRoster(),
    ]);
    const map = {};
    data.forEach(m => { map[m.meal_date] = m; });
    setMeals(map);
    setSaved(savedData);
    setRoster(rosterData);
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const startEdit = (dateStr) => {
    setEditing(dateStr);
    setInputVal(meals[dateStr]?.meal_name || '');
  };

  const saveEdit = async (val) => {
    const name = (val !== undefined ? val : inputVal).trim();
    if (!editing) return;
    if (name) {
      const updated = await setMeal(editing, name);
      setMeals(prev => ({ ...prev, [editing]: { ...prev[editing], ...updated } }));
      // Reload to get cook info
      const start = weekDates[0].str;
      const end   = weekDates[6].str;
      const data  = await fetchMeals(start, end);
      const map   = {};
      data.forEach(m => { map[m.meal_date] = m; });
      setMeals(map);
    } else {
      await deleteMeal(editing);
      setMeals(prev => { const n = {...prev}; delete n[editing]; return n; });
    }
    setEditing(null);
  };

  const cycleCook = async (dateStr, e) => {
    e.stopPropagation();
    const meal = meals[dateStr];
    if (!meal) return;
    const idx = roster.findIndex(r => r.id === meal.cook_id);
    const next = roster[(idx + 1) % roster.length];
    await setMealCook(dateStr, next.id);
    setMeals(prev => ({
      ...prev,
      [dateStr]: { ...prev[dateStr], cook_id: next.id, cook_name: next.name, cook_color: next.color }
    }));
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') saveEdit();
    if (e.key === 'Escape') setEditing(null);
  };

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <span style={styles.panelLabel}>This Week</span>
        <span style={styles.dot} />
      </div>

      <div style={styles.mealList}>
        {weekDates.map(({ str, day, isToday }) => {
          const meal = meals[str];
          const cook = meal?.cook_name
            ? { name: meal.cook_name, color: meal.cook_color }
            : null;

          return (
            <div key={str}>
              <div style={{
                ...styles.mealRow,
                ...(isToday ? styles.mealRowToday : {}),
              }}>
                {/* Day label */}
                <span style={{
                  ...styles.dayLabel,
                  ...(isToday ? styles.dayLabelToday : {}),
                }}>{day}</span>

                {/* Meal name */}
                {editing === str ? (
                  <input
                    autoFocus
                    style={styles.mealInput}
                    value={inputVal}
                    onChange={e => setInputVal(e.target.value)}
                    onBlur={() => saveEdit()}
                    onKeyDown={handleKey}
                    placeholder="What's for dinner?"
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span
                    style={meal ? styles.mealName : styles.mealEmpty}
                    onClick={() => startEdit(str)}
                  >
                    {meal?.meal_name || '—'}
                  </span>
                )}

                {/* Cook badge — only when meal exists */}
                {meal && cook && editing !== str && (
                  <div
                    style={styles.cookBadge}
                    onClick={(e) => roster.length > 1 && cycleCook(str, e)}
                    title={roster.length > 1 ? `Tap to change cook (${cook.name})` : cook.name}
                  >
                    <InitialBadge name={cook.name} color={cook.color} />
                    <span style={{ ...styles.cookName, color: cook.color }}>
                      {cook.name}
                    </span>
                  </div>
                )}
              </div>

              {/* Saved meal suggestions */}
              {editing === str && saved.length > 0 && (
                <div style={styles.suggestions}>
                  {saved.map(s => (
                    <button
                      key={s.id}
                      style={styles.suggestionChip}
                      onMouseDown={e => { e.preventDefault(); saveEdit(s.name); }}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  panel: {
    display: 'flex', flexDirection: 'column',
    gap: 8, height: '100%', overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexShrink: 0,
  },
  panelLabel: {
    fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
    letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-dimmer)',
  },
  dot: {
    width: 6, height: 6, borderRadius: '50%',
    background: 'var(--accent3)', boxShadow: '0 0 6px var(--accent3)',
  },
  mealList: {
    display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto', flex: 1,
  },
  mealRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '7px 10px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--surface2)',
    border: '1px solid transparent',
    minHeight: 38,
  },
  mealRowToday: {
    border: '1px solid var(--accent3)',
    background: 'rgba(122,232,184,0.06)',
  },
  dayLabel: {
    fontFamily: 'var(--font-mono)', fontSize: '0.68rem',
    color: 'var(--text-dim)', width: 28, flexShrink: 0, letterSpacing: '0.05em',
  },
  dayLabelToday: { color: 'var(--accent3)', fontWeight: 500 },
  mealName: {
    fontSize: '0.88rem', color: 'var(--text)', flex: 1, cursor: 'pointer',
  },
  mealEmpty: {
    fontSize: '0.88rem', color: 'var(--text-dimmer)', flex: 1, cursor: 'pointer',
  },
  mealInput: {
    flex: 1, background: 'transparent', border: 'none', outline: 'none',
    color: 'var(--text)', fontSize: '0.88rem', fontFamily: 'var(--font-body)', padding: 0,
  },
  cookBadge: {
    display: 'flex', alignItems: 'center', gap: 5,
    cursor: 'pointer', flexShrink: 0, marginLeft: 4,
  },
  cookName: {
    fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
    letterSpacing: '0.04em', whiteSpace: 'nowrap',
  },
  suggestions: {
    display: 'flex', flexWrap: 'wrap', gap: 5, padding: '5px 10px 3px',
  },
  suggestionChip: {
    fontFamily: 'var(--font-mono)', fontSize: '0.68rem',
    color: 'var(--accent3)', border: '1px solid rgba(122,232,184,0.3)',
    borderRadius: 20, padding: '3px 9px', background: 'transparent', cursor: 'pointer',
    letterSpacing: '0.03em',
  },
};
